import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { getClockWindowNameBasedOnSelectedOption } from '../../../services/shiftPlanService';
import { DEFAULT_SHIFT_HALF_OPTIONS, ROUTES } from '../../../utils/constants';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  clearStationAssignment,
  selectStationAssignment,
  setSelectedHalf,
} from '../../../store/slices/stationAssignmentSlice';
import { showError, showNotification } from '../../../store/slices/errorSlice';
import { isBlankString } from '../../../utils/string-utils';
import { useNavigationControl } from '../../../hooks/useNavigationControl';
import AppModal from '../../../components/AppModal/AppModal';
import StatsRow from './components/StatsRow';
import AssociateSidebar from './components/AssociateSidebar';
import ProcessPathSection from './components/ProcessPathSection';
import AssignAssociateModal, { AssignSelection } from './components/AssignAssociateModal';
import UnassignAssociateModal from './components/UnassignAssociateModal';
import DecisionInfoModal from './components/DecisionInfoModal';
import { getStationAssignments, publishStationAssignment } from './api';
import {
  AssignmentAssociate,
  DecisionSource,
  GetStationAssignmentsResponse,
  OverrideDecision,
  ProcessPath,
  StationAssignmentHalfData,
  isEmptySlot,
  isHalfEmpty,
} from './models';
import {
  applyDecisionToAssociate,
  describeAssignedLocation,
  fillEmptyNonWorkstationSlot,
  fillEmptyStationSlot,
  filterUnassignedAssociates,
  findAssignedAssociate,
  getPALeadHRPool,
  isAssigned,
  unassignAssociate,
} from './utils';
import './index.scss';

/**
 * Redesigned Station Assignment screen (work in progress).
 *
 * This is the new UX that will eventually replace the current Assignments
 * page (now `OldAssignmentsPage` under `../oldAssignments`). It reuses the
 * unchanged data layer — `getStationAssignments`, `models`, `utils` — from
 * this same `assignments/` folder, so only the presentation changes.
 *
 * Being built up one piece at a time. So far it fetches the current shift's
 * assignment data and renders the top-of-page {@link StatsRow} summary, the
 * {@link AssociateSidebar} of unassigned associates, every
 * {@link ProcessPathSection} (line lanes and pools), and a footer Verify
 * action that publishes the plan — the same submit flow as the current
 * Assignments page (confirm modal → `publishStationAssignment` → success
 * notification → return to the landing page). Assignment interactions
 * (assign/unassign/move) land in subsequent changes.
 */
const AssignmentsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { navigate } = useNavigationControl();
  const { selectedDate, selectedShift, selectedHalf, planId } =
    useAppSelector(selectStationAssignment);

  const [response, setResponse] = useState<GetStationAssignmentsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  // Independent search text for the PA/Lead/HR box so searching one box never
  // filters the other.
  const [paLeadHRSearchQuery, setPALeadHRSearchQuery] = useState<string>('');
  // Per-path expand/collapse state, keyed by fclmFunctionId. Missing entries
  // default to expanded, so paths start open until the user collapses them.
  const [expandedPaths, setExpandedPaths] = useState<Record<string, boolean>>({});
  // Confirmation modal shown when the user clicks Verify. Submitting from
  // the modal publishes the plan.
  const [verifyModalOpen, setVerifyModalOpen] = useState<boolean>(false);
  // The unassigned associate chosen from the sidebar "Assign" button. Non-null
  // means the AssignAssociateModal is open for that associate.
  const [assigningAssociate, setAssigningAssociate] = useState<AssignmentAssociate | null>(null);
  // When set, the AssignAssociateModal opens in preset mode: the destination
  // slot is already known (the planner picked an associate from a line's
  // inline "Add associate" typeahead), so the modal only captures a reason.
  const [presetSelection, setPresetSelection] = useState<AssignSelection | null>(null);
  // The assigned associate chosen from an occupied slot's "Unassign" action.
  // Non-null means the UnassignAssociateModal is open for that associate.
  const [unassigningAssociate, setUnassigningAssociate] = useState<AssignmentAssociate | null>(
    null,
  );
  // The associate whose assignment decision is being viewed (info ℹ️ action).
  // Non-null means the DecisionInfoModal is open for that associate.
  const [viewingDecisionAssociate, setViewingDecisionAssociate] =
    useState<AssignmentAssociate | null>(null);

  const shift = selectedShift ?? 'day';
  const half = selectedHalf || DEFAULT_SHIFT_HALF_OPTIONS[0].value;

  const fetchData = useCallback(async () => {
    if (!selectedDate || !planId) {
      setError('Missing required parameters (date or planId).');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const clockWindow = getClockWindowNameBasedOnSelectedOption(shift);
      const result = await getStationAssignments(selectedDate, clockWindow, planId);

      // Null-safe emptiness checks for each half. The facade sends an empty
      // (non-null) half object when the backend did not produce that half, but
      // `isHalfEmpty` also tolerates a missing/null half defensively so we
      // never dereference `firstHalfStationAssignment.associates` blindly.
      const firstHalfEmpty = isHalfEmpty(result.firstHalfStationAssignment);
      const secondHalfEmpty = isHalfEmpty(result.secondHalfStationAssignment);

      // Treat a response with no plan data in EITHER half and no
      // `lastModifiedBy` as an error surface, mirroring the current
      // Assignments page's behaviour. Checking both halves (rather than only
      // the first) means a legitimate second-half-only plan — first half empty
      // but second half populated — is not mistaken for a load failure.
      if (firstHalfEmpty && secondHalfEmpty && !result.lastModifiedBy) {
        setError('Failed to load assignments data. Please try again.');
        setResponse(null);
      } else {
        setResponse(result);
        // When the backend produced only the second half (plan generated after
        // the shift midpoint), the first half arrives empty. Default the half
        // selector to the populated second half so the operator lands on data
        // instead of a blank first-half board. This fires only on (re)fetch —
        // `selectedHalf` is not a dependency of `fetchData` — so a subsequent
        // manual switch back to the first half is preserved, not overridden.
        if (firstHalfEmpty && !secondHalfEmpty) {
          dispatch(setSelectedHalf('secondHalfStationAssignment'));
        }
      }
    } catch (err: any) {
      setError(err?.message || 'An unexpected error occurred.');
      setResponse(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, planId, shift]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const activeHalf: StationAssignmentHalfData | null = useMemo(() => {
    if (!response) return null;
    return response[
      half as keyof Pick<
        GetStationAssignmentsResponse,
        'firstHalfStationAssignment' | 'secondHalfStationAssignment'
      >
    ];
  }, [response, half]);

  /**
   * The unassigned pool: associates not present in any workstation or
   * non-workstation list for the active half. Same `isAssigned` filter the
   * sidebar relies on, so the count badge and StatsRow stay consistent.
   */
  const unassignedAssociates: AssignmentAssociate[] = useMemo(() => {
    if (!activeHalf) return [];
    return activeHalf.associates.filter(
      (a) => !isAssigned(a, activeHalf.processPaths),
    );
  }, [activeHalf]);

  const filteredUnassignedAssociates = useMemo(
    () => filterUnassignedAssociates(unassignedAssociates, searchQuery),
    [unassignedAssociates, searchQuery],
  );

  /**
   * The PA/Lead/HR pool: associates the backend excluded from workstation
   * assignment (jobLevel >= 2), surfaced as a separate box below the Unassigned
   * box. Derived with the same `isAssigned` filter so assigned PA/Lead/HR
   * associates (assigned via the box, now living in a path) drop out of the box.
   */
  const paLeadHRAssociates: AssignmentAssociate[] = useMemo(
    () => (activeHalf ? getPALeadHRPool(activeHalf) : []),
    [activeHalf],
  );

  const filteredPALeadHRAssociates = useMemo(
    () => filterUnassignedAssociates(paLeadHRAssociates, paLeadHRSearchQuery),
    [paLeadHRAssociates, paLeadHRSearchQuery],
  );

  /**
   * Headline counts for the StatsRow. `assigned` is counted from the
   * authoritative path/workstation structure; `unassigned` reuses the
   * derived pool above; `scheduled` is the sum of the two so the total holds
   * regardless of whether the response keeps assigned associates in the
   * top-level pool.
   */
  const stats = useMemo(() => {
    if (!activeHalf) {
      return { scheduled: 0, assigned: 0, unassigned: 0, overrides: 0 };
    }
    // Count only real associates — empty (planned-but-unfilled) slots appear
    // in these lists as placeholder entries and must not inflate "assigned".
    const countReal = (entries: AssignmentAssociate[]) =>
      entries.filter((a) => !isEmptySlot(a)).length;
    const assigned = activeHalf.processPaths.reduce(
      (sum, path) =>
        sum +
        path.workstations.reduce((wsSum, ws) => wsSum + countReal(ws.assignedAssociates), 0) +
        countReal(path.nonWorkstationAssociates ?? []),
      0,
    );
    const unassigned = unassignedAssociates.length;
    return {
      scheduled: assigned + unassigned,
      assigned,
      unassigned,
      // Overrides are tracked per session once the assignment interactions
      // land in a later slice; there is no override count to show yet.
      overrides: 0,
    };
  }, [activeHalf, unassignedAssociates]);

  /**
   * Every process path for the active half, rendered in response order. Each
   * {@link ProcessPathSection} adapts its body to how the function is staffed
   * (line lanes vs. an associate pool).
   */
  const processPaths: ProcessPath[] = activeHalf?.processPaths ?? [];

  /**
   * Label for the Assign popup's preset (inline typeahead) destination: the
   * chosen empty slot's per-station id when available, otherwise the
   * workstation id. `undefined` when the modal isn't in preset mode.
   */
  const presetDestinationLabel = useMemo<string | undefined>(() => {
    if (!presetSelection) return undefined;
    // Non-workstation (pool) destination: label with the function's mapping /
    // path name since there is no per-station id.
    if (presetSelection.nonWorkstation) {
      const path = processPaths.find(
        (p) => p.fclmFunctionId === presetSelection.fclmFunctionId,
      );
      return path?.processPathMappingName ?? path?.processPathName ?? presetSelection.fclmFunctionId;
    }
    for (const path of processPaths) {
      for (const ws of path.workstations) {
        if (ws.workstationId === presetSelection.workstationId) {
          return (
            ws.assignedAssociates[presetSelection.slotIndex]?.assignedWorkstationId ??
            presetSelection.workstationId
          );
        }
      }
    }
    /* istanbul ignore next -- defensive: a preset selection always targets a rendered workstation */
    return presetSelection.workstationId;
  }, [presetSelection, processPaths]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handlePALeadHRSearchChange = (value: string) => {
    setPALeadHRSearchQuery(value);
  };

  /**
   * Add an unassigned associate to a specific empty slot on a line. Delegates
   * to the pure {@link fillEmptyStationSlot} helper and swaps the active half
   * into `response`; the sidebar, count badge, and StatsRow all re-derive from
   * this state, so they update automatically. No override validation in this
   * first cut — the pick is committed directly.
   */
  /**
   * Handle a pick from a line's inline "Add associate" typeahead. Rather than
   * committing immediately, open the Assign popup in *preset* mode (destination
   * already known) so the planner must capture a reason — mirroring the sidebar
   * Assign and Move flows. The actual placement + decision stamping happens in
   * {@link handleAssignModalSubmit} on confirm.
   */
  const handleAssignToSlot = (
    associateId: string,
    workstationId: string,
    slotIndex: number,
  ) => {
    /* istanbul ignore if -- defensive: the slot only renders once response/activeHalf exist */
    if (!activeHalf) return;
    const associate = unassignedAssociates.find((a) => a.associateId === associateId) ?? null;
    /* istanbul ignore if -- defensive: the typeahead only offers unassigned associates */
    if (!associate) return;
    // Resolve the owning process path so the selection carries its function id.
    const owningPath = activeHalf.processPaths.find((path) =>
      path.workstations.some((ws) => ws.workstationId === workstationId),
    );
    /* istanbul ignore if -- defensive: the slot always belongs to a rendered path */
    if (!owningPath) return;
    setAssigningAssociate(associate);
    setPresetSelection({
      fclmFunctionId: owningPath.fclmFunctionId,
      workstationId,
      slotIndex,
    });
  };

  /**
   * Handle a pick from a no-lane pool's inline "Add associate" typeahead
   * (a non-workstation slot in the function's `nonWorkstationAssociates`).
   * Opens the Assign popup in preset mode — the destination is already known,
   * so only a reason is captured — and marks the selection `nonWorkstation` so
   * {@link handleAssignModalSubmit} routes it to
   * {@link fillEmptyNonWorkstationSlot}.
   */
  const handleAssignToNonWorkstationSlot = (
    associateId: string,
    fclmFunctionId: string,
    slotIndex: number,
  ) => {
    /* istanbul ignore if -- defensive: the slot only renders once activeHalf exists */
    if (!activeHalf) return;
    const associate = unassignedAssociates.find((a) => a.associateId === associateId) ?? null;
    /* istanbul ignore if -- defensive: the typeahead only offers unassigned associates */
    if (!associate) return;
    setAssigningAssociate(associate);
    setPresetSelection({
      fclmFunctionId,
      workstationId: '',
      slotIndex,
      nonWorkstation: true,
    });
  };

  /**
   * Open the Assign popup for an unassigned associate picked from either the
   * Unassigned sidebar or the PA/Lead/HR box.
   */
  const handleOpenAssignModal = (associateId: string) => {
    const associate =
      unassignedAssociates.find((a) => a.associateId === associateId) ??
      paLeadHRAssociates.find((a) => a.associateId === associateId) ??
      null;
    setAssigningAssociate(associate);
  };

  /**
   * Open the Assign popup to *move* an already-assigned associate (the ↗
   * action on an occupied slot / pool card). Resolves the associate from
   * wherever they currently sit and reuses the same cascade + reason modal;
   * the submit path ({@link handleAssignModalSubmit}) relies on
   * {@link fillEmptyStationSlot} removing them from their prior location, so
   * the move never round-trips through the unassigned pool.
   */
  const handleOpenMoveModal = (associateId: string) => {
    /* istanbul ignore if -- defensive: an occupied slot only renders once activeHalf exists */
    if (!activeHalf) return;
    setAssigningAssociate(findAssignedAssociate(activeHalf.processPaths, associateId));
  };

  const handleCloseAssignModal = () => {
    setAssigningAssociate(null);
    setPresetSelection(null);
  };

  /**
   * Open the read-only Decision info popup for an assigned associate (the ℹ️
   * action). Resolves the associate from wherever they currently sit so the
   * popup can read their `decision`.
   */
  const handleOpenDecisionModal = (associateId: string) => {
    /* istanbul ignore if -- defensive: an occupied slot only renders once activeHalf exists */
    if (!activeHalf) return;
    // Resolve from an assigned location first; fall back to the PA/Lead/HR pool
    // so the info (ℹ️) action works on an as-yet-unassigned PA/Lead/HR associate
    // (whose decision carries PA_LEAD_HR_EXCLUDED).
    const associate =
      findAssignedAssociate(activeHalf.processPaths, associateId) ??
      paLeadHRAssociates.find((a) => a.associateId === associateId) ??
      null;
    setViewingDecisionAssociate(associate);
  };

  const handleCloseDecisionModal = () => {
    setViewingDecisionAssociate(null);
  };

  /**
   * Commit an assignment made through the Assign popup: fill the chosen empty
   * slot with the associate and stamp the captured `USER_OVERRIDE` decision so
   * the action travels to the facade on publish. Delegates placement to the
   * same pure {@link fillEmptyStationSlot} helper as the inline typeahead.
   */
  const handleAssignModalSubmit = (selection: AssignSelection, override: OverrideDecision) => {
    /* istanbul ignore if -- defensive: modal only opens once response/activeHalf and an associate exist */
    if (!response || !activeHalf || !assigningAssociate?.associateId) return;
    const associateId = assigningAssociate.associateId;

    const filled = selection.nonWorkstation
      ? fillEmptyNonWorkstationSlot(
          activeHalf,
          associateId,
          selection.fclmFunctionId,
          selection.slotIndex,
        )
      : fillEmptyStationSlot(
          activeHalf,
          associateId,
          selection.workstationId,
          selection.slotIndex,
        );

    // Record the planner's action as a USER_OVERRIDE decision on the moved
    // associate so the captured reason flows to the facade on publish.
    const stamped = applyDecisionToAssociate(filled, associateId, {
      source: DecisionSource.USER_OVERRIDE,
      reasonCode: override.reasonCode,
      decisionReason: override.decisionReason,
    });

    setResponse({ ...response, [half]: stamped });
    setAssigningAssociate(null);
    setPresetSelection(null);
  };

  /**
   * Open the Unassign popup for an assigned associate picked from an occupied
   * slot. Resolves the associate from wherever they currently sit.
   */
  const handleOpenUnassignModal = (associateId: string) => {
    /* istanbul ignore if -- defensive: an occupied slot only renders once activeHalf exists */
    if (!activeHalf) return;
    setUnassigningAssociate(findAssignedAssociate(activeHalf.processPaths, associateId));
  };

  const handleCloseUnassignModal = () => {
    setUnassigningAssociate(null);
  };

  /**
   * Commit an unassignment made through the Unassign popup: return the
   * associate to the unassigned pool (leaving an empty slot on a line, or
   * removing them from a department list) and stamp the captured
   * `USER_OVERRIDE` decision so the action travels to the facade on publish.
   */
  const handleUnassignModalSubmit = (override: OverrideDecision) => {
    /* istanbul ignore if -- defensive: modal only opens once response/activeHalf and an associate exist */
    if (!response || !activeHalf || !unassigningAssociate?.associateId) return;
    const associateId = unassigningAssociate.associateId;

    const removed = unassignAssociate(activeHalf, associateId);
    const stamped = applyDecisionToAssociate(removed, associateId, {
      source: DecisionSource.USER_OVERRIDE,
      reasonCode: override.reasonCode,
      decisionReason: override.decisionReason,
    });

    setResponse({ ...response, [half]: stamped });
    setUnassigningAssociate(null);
  };

  const handleToggleExpanded = (fclmFunctionId: string) => {
    setExpandedPaths((prev) => ({
      ...prev,
      [fclmFunctionId]: !(prev[fclmFunctionId] ?? true),
    }));
  };

  const handleVerifyClick = () => {
    // Open the "Are you sure you want to submit the plan?" confirmation
    // modal. The actual submission happens in `handleSubmitPlan` below.
    setVerifyModalOpen(true);
  };

  const handleVerifyModalBack = () => {
    setVerifyModalOpen(false);
  };

  const handleSubmitPlan = async () => {
    // Close the confirmation modal first so it doesn't stack with the
    // global success notification rendered by ErrorModal.
    setVerifyModalOpen(false);

    /* istanbul ignore if -- defensive: planId is always set when page loads via Redux */
    if (!planId) {
      dispatch(
        showError({
          title: 'Publishing Error',
          message: 'Plan ID is required to publish.',
        }),
      );
      return;
    }

    /* istanbul ignore if -- defensive: a blank date is blocked at load by fetchData, so the Verify button never renders without one */
    if (isBlankString(selectedDate ?? undefined)) {
      dispatch(
        showNotification({
          title: 'Missing Selection',
          message: 'A date is required to publish the station assignment plan.',
          type: 'warning',
        }),
      );
      return;
    }

    try {
      await publishStationAssignment(
        planId,
        response!,
        selectedDate as string,
        getClockWindowNameBasedOnSelectedOption(shift),
      );

      // The success popup mirrors the Shift Plan publish flow: dispatch a
      // success notification (a Modal with an "OK" button rendered by the
      // global ErrorModal). Navigate to the initial Station Assignment
      // landing page so when the user clicks OK they're already there.
      dispatch(
        showNotification({
          title: 'Plan Submitted',
          message: 'Station assignment plan submitted successfully!',
          type: 'success',
        }),
      );

      // Reset the workflow state and return to the initial landing page so
      // the user can start a new plan.
      dispatch(clearStationAssignment());
      navigate(ROUTES.SHIFT_ROTATION);
    } catch (err: any) {
      dispatch(
        showError({
          title: 'Publishing Error',
          message: err.message || 'Failed to publish station assignment plan. Please try again.',
        }),
      );
    }
  };

  if (loading) {
    return (
      <Box className="assignments-page" spacingInset="400">
        <Column
          className="assignments-page__loading"
          alignmentHorizontal="center"
          alignmentVertical="center"
          data-testid="assignments-loader"
        >
          <Loader />
        </Column>
      </Box>
    );
  }

  if (error || !response) {
    return (
      <Box className="assignments-page" spacingInset="400">
        <Alert type="error" data-testid="assignments-error">
          {error ?? 'Failed to load assignments data.'}
        </Alert>
      </Box>
    );
  }

  /**
   * Whether the operator is viewing an empty first half that exists only
   * because the plan was generated after the shift midpoint (the backend
   * produced only the second half). The half selector auto-defaults to the
   * populated second half on load, so this notice is seen only when the
   * operator deliberately switches back to the first half — it explains why
   * the board is blank ("not planned") instead of reading as a load failure.
   *
   * Caveat: the facade sends an empty half object both for a skipped first
   * half and for one that is genuinely empty for other reasons, so the UI
   * cannot tell them apart. It infers the skipped case from a populated second
   * half. If the distinction ever needs to be exact, the facade must add an
   * explicit flag (e.g. `firstHalfStatus: "SKIPPED" | "PLANNED"`).
   */
  const showFirstHalfSkippedNotice =
    half === 'firstHalfStationAssignment' &&
    isHalfEmpty(response.firstHalfStationAssignment) &&
    !isHalfEmpty(response.secondHalfStationAssignment);

  return (
    <Box className="assignments-page" spacingInset="400">
      <StatsRow
        scheduled={stats.scheduled}
        assigned={stats.assigned}
        unassigned={stats.unassigned}
        overrides={stats.overrides}
      />

      {/*
        Plain flex layout (matching the previous Assignments page): a
        fixed-width sidebar column and a filling main column. Meridian's Row
        width handling did not reliably pin the sidebar, so we use the proven
        flexbox approach here and keep Meridian for the content within.
      */}
      <div className="assignments-page__layout">
        <div className="assignments-page__sidebar">
          <AssociateSidebar
            unassignedAssociates={filteredUnassignedAssociates}
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            onAssignAssociate={handleOpenAssignModal}
          />
          {paLeadHRAssociates.length > 0 && (
            <AssociateSidebar
              title="PA / Lead / HR"
              unassignedAssociates={filteredPALeadHRAssociates}
              searchQuery={paLeadHRSearchQuery}
              onSearchChange={handlePALeadHRSearchChange}
              onAssignAssociate={handleOpenAssignModal}
              onViewDecision={handleOpenDecisionModal}
            />
          )}
        </div>

        <div className="assignments-page__main">
          {showFirstHalfSkippedNotice ? (
            <Alert type="neutral" data-testid="assignments-first-half-not-planned">
              The first half of this shift was not planned because the plan was
              generated after the shift midpoint. Select &ldquo;Second half&rdquo; to
              view the assignment.
            </Alert>
          ) : (
            processPaths.map((path) => (
              <ProcessPathSection
                key={path.fclmFunctionId}
                processPath={path}
                expanded={expandedPaths[path.fclmFunctionId] ?? true}
                onToggleExpanded={handleToggleExpanded}
                unassignedAssociates={unassignedAssociates}
                onAssignToSlot={handleAssignToSlot}
                onAssignToNonWorkstationSlot={handleAssignToNonWorkstationSlot}
                onUnassign={handleOpenUnassignModal}
                onMove={handleOpenMoveModal}
                onViewDecision={handleOpenDecisionModal}
              />
            ))
          )}
        </div>
      </div>

      {/* Footer: Last Modified info + Verify action, mirroring the current
          Assignments page. A Meridian Row pins the info to the left and the
          Verify button to the right. */}
      <Row
        className="assignments-page__footer"
        alignmentVertical="center"
        widths={['fill', 'fit']}
      >
        <Text color="secondary" data-testid="assignments-last-modified">
          Last Modified: {response.lastModifiedDate} ({response.lastModifiedBy})
        </Text>
        <Button
          type="primary"
          onClick={handleVerifyClick}
          data-testid="assignments-verify-button"
        >
          Verify
        </Button>
      </Row>

      {/* Verify-confirmation modal. Asks the user to confirm before submitting
          the assignment plan. "Back" returns to the page in its current state;
          "Submit" publishes the plan and navigates to the landing page. Uses
          the shared AppModal so the look matches other two-button confirmations
          across the app. */}
      <AppModal
        open={verifyModalOpen}
        title="Submit plan"
        message="Are you sure you want to submit the plan?"
        variant="confirmation"
        primaryLabel="Submit"
        secondaryLabel="Back"
        onPrimary={handleSubmitPlan}
        onSecondary={handleVerifyModalBack}
        onClose={handleVerifyModalBack}
        data-testid="assignments-verify-modal"
      />

      {/* Assign popup opened from the sidebar "Assign" button. Cascades
          Assign to → Line → Station and captures a required reason; on submit
          it fills the chosen slot and stamps the USER_OVERRIDE decision. */}
      <AssignAssociateModal
        open={assigningAssociate !== null}
        associate={assigningAssociate}
        processPaths={processPaths}
        presetSelection={presetSelection ?? undefined}
        presetDestinationLabel={presetDestinationLabel}
        onClose={handleCloseAssignModal}
        onSubmit={handleAssignModalSubmit}
      />

      {/* Unassign popup opened from an occupied slot's ✕ action. Captures a
          required reason; on submit it returns the associate to the pool and
          stamps the USER_OVERRIDE decision. */}
      <UnassignAssociateModal
        open={unassigningAssociate !== null}
        associate={unassigningAssociate}
        currentLocationLabel={
          unassigningAssociate?.associateId
            ? describeAssignedLocation(processPaths, unassigningAssociate.associateId) ?? undefined
            : undefined
        }
        onClose={handleCloseUnassignModal}
        onSubmit={handleUnassignModalSubmit}
      />

      {/* Read-only decision info popup opened from an occupied slot's ℹ️
          action. Shows the associate's decision source + reason code (and
          optional free-text reason). */}
      <DecisionInfoModal
        open={viewingDecisionAssociate !== null}
        associate={viewingDecisionAssociate}
        onClose={handleCloseDecisionModal}
      />
    </Box>
  );
};

export default AssignmentsPage;

import './index.scss';